import React from 'react';
import {formatPhoneNumber, formatNumber, formatDate, formatAgentPercent, percent,formatDateTime,viewDateTimeFormat} from "../../utils";
import { phoneCall, usePhoneAvailable } from "../../utils/phone";
import PropTypes from "prop-types";

require('./Input.scss');

const Input = ({theme = 'default', id, value, inputType, valueType = 'text', label, handleParentChange, handleParentBlur, decimals = 0, maxDecimals = 0, style, viewMode = false, deleted = false, className = "", isDollar = false, withCommas = false, tooltip, tooltipPosition='up', handleInputFocus, onInputClick, hyperlink = false, hyperlinkLabel, onKeyUp, name, ...rest}) => {
    const phoneAvailable = usePhoneAvailable();

    const formatValue = (value, type) => {
        switch (type) {
            case 'number':
            case 'float':   // with commas and decimals
                return formatNumber(value, isDollar || withCommas);
            case 'integer': // with commas
            case 'years':
            case 'percent':
                return formatNumber(value, isDollar || withCommas);
            case 'phone':
                return formatPhoneNumber(value);
            case 'date':
                return formatDate(value);
            case 'datetime':
                return formatDateTime(value);                
            case 'agent-percent':
                return formatAgentPercent(value);
            default:
                return value;
        }
    };

    const formatViewValue = () => {
        if (!value) return '-';
        switch (valueType) {
            case 'number':
                return formatNumber(value);
            case 'integer':
                return `${isDollar ? '$' : ''}${formatNumber(value, isDollar || withCommas)}`;
            case 'float':
                return `${isDollar ? '$' : ''}${formatNumber(value, isDollar || withCommas, decimals)}`;
            case 'phone':
                return formatPhoneNumber(value);
            case 'date':
                return formatDate(value);
            case 'datetime':
                return viewDateTimeFormat(value); 
            case 'percent':
            case 'agent-percent':
                return percent(value, decimals, maxDecimals);
            case 'years':
                return `${value} Years`;
            default:
                return value;
        }
    };
    
    const handleChange = (e) => {
        const key = e.currentTarget.name;
        const value = formatValue(e.currentTarget.value, valueType);
        handleParentChange(key, value);
        return value;
    };

    const handleBlur = (e) => {
        const key = e.currentTarget.name;
        const value = formatValue(e.currentTarget.value, valueType);
        handleParentBlur(key, value);
        return value;
    };

    if (!decimals)
        decimals = valueType === 'percent' ? 2 : 0;
    const disabled = rest.disabled;
    const isPercent = valueType === 'percent';
    const agentPercent = valueType === 'agent-percent';
    const val = formatValue(value, valueType);
    const viewVal = formatViewValue();
    if (valueType === 'email') rest.type = 'email';
    if (valueType === 'phone') rest.type = 'tel';

    return (
        <div id={id} className={`input-container ${theme}${agentPercent ? ' agent-percent' : ''} ${className}`}>
            {
                label &&
                <label className={`input-label ${theme}${disabled ? ' disabled' : ''}`}>{label}</label>
            }

            {
                viewMode && tooltip &&
                <span className={`tooltip-new ${tooltipPosition ? tooltipPosition:''}`}>{tooltip}</span>
            }

            {
                !viewMode &&
                <div className={`input-wrapper  ${onInputClick ? 'clickable' : ''} ${inputType!=='textarea' ? " " : "text-wrapper"}`} onClick={onInputClick}>
                    {
                        inputType!=='textarea' ?
                        <input className={`input ${theme}${disabled ? ' disabled' : ''}${isPercent ? ' percent' : ''}${agentPercent ? ' agent-percent' : ''}`}
                           style={style}
                           name={name}
                           value={val || ''}
                           onChange={handleChange}
                           onBlur = {handleParentBlur ? handleBlur : null}
                           onFocus={handleInputFocus}
                           onKeyUp={onKeyUp}
                           {...rest}
                    />
                    :   <textarea className={`input ${theme}${disabled ? ' disabled' : ''}${isPercent ? ' percent' : ''}${agentPercent ? ' agent-percent' : ''}`}
                    style={style}
                    name={name}
                    value={val || ''}
                    rows={5}
                    onChange={handleChange}
                    onBlur = {handleParentBlur ? handleBlur : null}
                    onFocus={handleInputFocus}
                    onKeyUp={onKeyUp}
                    {...rest}
             /> }
                    {
                        (isPercent || agentPercent) &&
                        <React.Fragment>
                            {
                                agentPercent &&
                                <span className={`percentage${!value ? ' no-value' : ''}`}>%</span>
                            }
                            <div className={`input-display-value ${theme}${disabled ? ' disabled' : ''}${isPercent ? ' percent' : ''}${agentPercent ? ' agent-percent' : ''}${!value ? ' no-value' : ''}`}>
                                {value ? (maxDecimals > decimals
                                    ? Number(value).toLocaleString(undefined, {minimumFractionDigits: decimals, maximumFractionDigits: maxDecimals})
                                    : Number(value).toFixed(decimals)) : ''}
                            </div>
                        </React.Fragment>
                    }
                </div>
            }
            {
                (viewMode && 
                    (!hyperlink ? <div className={`view ${theme}${disabled ? ' disabled': ''}${deleted ? ' input-view-mode-deleted' : ''} ${onInputClick ? 'clickable' : ''}`} onClick={onInputClick} style={valueType === 'phone' ? { display: 'flex', alignItems: 'center', gap: '6px' } : undefined}>
                        {viewVal}
                        {valueType === 'phone' && value && phoneAvailable && (
                            <button
                                title={`Call ${viewVal}`}
                                onClick={(e) => { e.stopPropagation(); phoneCall(value); }}
                                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0 2px', lineHeight: 1, fontSize: '14px', color: '#1976d2', flexShrink: 0 }}
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M6.62 10.79a15.05 15.05 0 0 0 6.59 6.59l2.2-2.2a1 1 0 0 1 1.01-.24 11.47 11.47 0 0 0 3.58.57 1 1 0 0 1 1 1v3.5a1 1 0 0 1-1 1A17 17 0 0 1 3 4a1 1 0 0 1 1-1h3.5a1 1 0 0 1 1 1c0 1.25.2 2.45.57 3.58a1 1 0 0 1-.25 1.01l-2.2 2.2z"/></svg>
                            </button>
                        )}
                    </div> : <a href={value} target="_blank">{hyperlinkLabel || value}</a>)
                )

            }
        </div>
    )
};

Input.propTypes = {
    theme: PropTypes.oneOf([
        'default',
        'white',
        'dark',
        'without-border'
    ]),
    id: PropTypes.string,
    value: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
        PropTypes.object
    ]),
    valueType: PropTypes.oneOf([
        'text',
        'number',
        'integer',
        'float',
        'phone',
        'email',
        'date',
        'percent',
        'agent-percent',
        'years'
    ]),
    label: PropTypes.string,
    handleParentChange: PropTypes.func,
    handleParentBlur: PropTypes.func,
    decimals: PropTypes.number,
    maxDecimals: PropTypes.number,
    style: PropTypes.object,
    viewMode: PropTypes.bool,
    disabled: PropTypes.bool,
    deleted: PropTypes.bool,
    className: PropTypes.string,
    isDollar: PropTypes.bool,
    withCommas: PropTypes.bool,
    tooltip: PropTypes.string,  
    tooltipPosition: PropTypes.oneOf(['up','down']),
    handleInputFocus: PropTypes.func,
    onInputClick: PropTypes.func
};

export default Input;